/**
 * Builds the published briefing document.
 *
 * This lives in `shared/` deliberately: it is imported by BOTH the Cloud
 * Function that publishes (functions/) and the in-app preview (client/), so
 * what a reviewer previews is byte-for-byte what gets published. Before this,
 * publish pushed the editor's bare fragment and the website dropped it into an
 * `<iframe srcDoc>` - which inherits no styles - so the live page rendered in
 * browser defaults while the preview applied Tailwind `prose`. Two renderings
 * of one document, and the public one was the unstyled one.
 *
 * Constraints that shaped this:
 *   - SELF-CONTAINED. It may be rendered inside an iframe, served as a static
 *     file, or saved to disk. It cannot depend on a host page's CSS, and it
 *     cannot reach for a CDN (the site sets a strict CSP). So: one inline
 *     <style>, system fonts, no external requests.
 *   - CHARSET DECLARED. Without it the em dashes and middots in the source
 *     arrived as mojibake, taking their encoding from whatever embedded them.
 *   - DETERMINISTIC. No timestamps, no randomness - preview and publish must
 *     produce identical bytes for identical input.
 */

export interface BriefingDocumentInput {
  readonly title: string;
  /** The editor's HTML fragment. Sanitize BEFORE calling: this only wraps. */
  readonly htmlContent: string;
  /** Rendered under the title when present. */
  readonly description?: string;
}

/** Escapes text destined for markup context (title, meta). */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * OfficeHub typography, expressed as plain CSS.
 *
 * Deliberately NOT Tailwind `prose`: the published file has no build step and
 * no stylesheet to link, so every rule it needs must be in the file. Inter is
 * requested but degrades to the system stack rather than blocking on a webfont
 * that CSP would refuse anyway.
 */
const BRIEFING_CSS = `
:root {
  --ink: #0f172a;
  --muted: #475569;
  --hairline: #cbd5e1;
  --accent: #007ACC;
  --paper: #ffffff;
}
* { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; }
body {
  margin: 0;
  background: var(--paper);
  color: var(--ink);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 17px;
  line-height: 1.7;
  -webkit-font-smoothing: antialiased;
}
.briefing {
  max-width: 46rem;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
}
.briefing__header { margin-bottom: 2.5rem; }
.briefing__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0;
}
.briefing__description {
  color: var(--muted);
  font-size: 1.0625rem;
  margin: 0.75rem 0 0;
}
.briefing__rule {
  border: 0;
  border-top: 1px solid var(--hairline);
  margin: 2rem 0 0;
}
.briefing__body > :first-child { margin-top: 0; }
.briefing__body h1, .briefing__body h2, .briefing__body h3 {
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  margin: 2.5rem 0 0.75rem;
}
.briefing__body h1 { font-size: 1.875rem; }
.briefing__body h2 { font-size: 1.5rem; }
.briefing__body h3 { font-size: 1.25rem; }
.briefing__body p { margin: 0 0 1.25rem; }
.briefing__body a { color: var(--accent); text-decoration: underline; text-underline-offset: 2px; }
.briefing__body ul, .briefing__body ol { margin: 0 0 1.25rem; padding-left: 1.5rem; }
.briefing__body li { margin: 0.375rem 0; }
.briefing__body blockquote {
  margin: 1.75rem 0;
  padding: 0.25rem 0 0.25rem 1.25rem;
  border-left: 3px solid var(--accent);
  color: var(--muted);
  font-style: italic;
}
.briefing__body img { max-width: 100%; height: auto; border-radius: 0.5rem; }
.briefing__body table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
}
.briefing__body th, .briefing__body td {
  border: 1px solid var(--hairline);
  padding: 0.5rem 0.75rem;
  text-align: left;
}
.briefing__body th { background: #f1f5f9; font-weight: 600; }
.briefing__body pre {
  background: #f1f5f9;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  font-size: 0.875rem;
}
.briefing__body code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.9em; }
.briefing__body hr { border: 0; border-top: 1px solid var(--hairline); margin: 2.5rem 0; }
@media (max-width: 640px) {
  body { font-size: 16px; }
  .briefing { padding: 2rem 1.25rem 3.5rem; }
  .briefing__title { font-size: 1.75rem; }
}
@media print {
  .briefing { max-width: none; padding: 0; }
  .briefing__body a { color: var(--ink); }
}
`.trim();

export function buildBriefingDocument(input: BriefingDocumentInput): string {
  const title = escapeHtml(input.title);
  const description = input.description?.trim()
    ? `<p class="briefing__description">${escapeHtml(input.description.trim())}</p>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${title}</title>
<style>${BRIEFING_CSS}</style>
</head>
<body>
<article class="briefing">
<header class="briefing__header">
<h1 class="briefing__title">${title}</h1>
${description}
<hr class="briefing__rule">
</header>
<div class="briefing__body">${input.htmlContent}</div>
</article>
</body>
</html>`;
}
